import PropTypes from "prop-types";
import React, { useEffect } from "react";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { validate } from "../../routes/AdminRoutes";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import Select from "../../components/FormikFields/Select";
import { TextField } from "formik-mui";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import { Button, Grid, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  fetchShopTypes,
  fetchOrderFilterLocations,
  fetchEmployeesByLocation,
  addOrderALaCartShop
} from "../../redux/actions/adminConsole/validate/validateActions";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import makeStyles from "@mui/styles/makeStyles";
import { Tooltip } from "../../components/Tooltip";

const useStyles = makeStyles(theme => ({
  alignButtonsBottom: {
    marginTop: "auto",
    marginLeft: "auto"
  }
}));

const getOrderSchema = t =>
  Yup.object().shape({
    shopLocation: Yup.string().required(
      t("validationMessages.shopLocationRequired")
    ),
    shopType: Yup.string().required(t("validationMessages.shopTypeRequired")),
    employee: Yup.string(),
    comment: Yup.string().max(
      350,
      t("validationMessages.cannotExceedLength", {
        type: t("orderALaCartShop.addOrderALaCartShopCommentField"),
        maxLength: 350
      })
    )
  });

function validateComments(value, employee, t) {
  let error;
  if (employee && employee !== "" && !value) {
    error = t("orderALaCartShop.addOrderALaCartShopCommentFieldRequired");
  }
  return error;
}

const commentMaxCharacters = 350;

const AddNewOrderForm = ({
  shopTypes,
  orderCartLocations,
  isSubmitting,
  values,
  submitForm,
  cancel,
  errors,
  touched
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();

  const { employeesByLocation, loading } = useSelector(({ validateState }) => {
    return {
      employeesByLocation: validateState.employeesByLocation,
      loading: validateState.employeesLoading
    };
  });

  useEffect(() => {
    if (values.shopLocation) {
      values.employee = "";
      dispatch(fetchEmployeesByLocation(values.shopLocation));
    }
  }, [values.shopLocation]);

  useEffect(() => {
    if (values.shopType === "1") values.employee = "";
  }, [values.shopType]);

  return (
    <Box
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        marginTop: "2%"
      }}
    >
      <Form>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Select
              name="shopType"
              menuItems={
                shopTypes
                  ? shopTypes.map(shopType => ({
                      value: shopType.id,
                      label: shopType.description
                    }))
                  : []
              }
              label={t("orderALaCartShop.selectShopType")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Select
              name="shopLocation"
              menuItems={
                orderCartLocations
                  ? orderCartLocations.map(orderCartLocation => ({
                      value: orderCartLocation.id,
                      label: orderCartLocation.name
                    }))
                  : []
              }
              label={t("orderALaCartShop.selectShopLocation")}
            />
          </Grid>
          <Tooltip
            title={
              values.shopType === "1"
                ? t("orderALaCartShop.employeeForInternetShopTooltip")
                : ""
            }
          >
            <Grid item xs={12} sm={6}>
              <Select
                name="employee"
                loading={loading}
                disabled={
                  values.shopType === "1" || loading || !values.shopLocation
                } //Employee dropdown should always be disabled if the shop type is internet
                menuItems={
                  employeesByLocation
                    ? employeesByLocation.map(employeeByLocation => ({
                        value: employeeByLocation.id,
                        label: employeeByLocation.fullName
                      }))
                    : []
                }
                label={t("orderALaCartShop.selectEmployee")}
              />
            </Grid>
          </Tooltip>
          <Grid item xs={12}>
            <Field
              variant="filled"
              fullWidth
              label={t("orderALaCartShop.enterNotes")}
              component={TextField}
              name="comment"
              multiline
              helperText={`${commentMaxCharacters -
                values.comment.length}/${commentMaxCharacters} ${t(
                "orderALaCartShop.addOrderALaCartShopCommentHelperText"
              )}`}
              rows={4}
              validate={value => validateComments(value, values.employee, t)}
            />
          </Grid>
        </Grid>
      </Form>
      <Box className={classes.alignButtonsBottom}>
        <ButtonGroup>
          <Button color="primary" onClick={cancel} disabled={isSubmitting}>
            {t("orderALaCartShop.cancelOrderALACartShopButton")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={submitForm}
            disabled={isSubmitting}
          >
            {t("orderALaCartShop.addOrderALaCartShopButton")}
          </Button>
        </ButtonGroup>
      </Box>
    </Box>
  );
};

AddNewOrderForm.propTypes = {
  shopTypes: PropTypes.any,
  orderCartLocations: PropTypes.any,
  values: PropTypes.any
};

export default function OrderALaCartShop() {
  const dispatch = useDispatch();

  const handleGoBack = () => {
    dispatch(push(validate));
  };
  const {
    shopTypes,
    orderCartLocations,
    loading,
    orderCartLocationsLoading
  } = useSelector(state => state.validateState);
  useEffect(() => {
    dispatch(fetchShopTypes({ active: true }));
    dispatch(fetchOrderFilterLocations());
  }, []);

  const handleAddOrderALaCartShop = async newOrderALaCartShopData => {
    await dispatch(addOrderALaCartShop(newOrderALaCartShopData));
    handleGoBack();
  };

  const { t } = useTranslation();

  if (loading && orderCartLocationsLoading) {
    return (
      <Box display="flex" flex="1" alignItems="center" justifyContent="center">
        <LoadingSpinner />
      </Box>
    );
  } else
    return (
      <AdminPageLayout
        title={<Breadcrumb routes={["Validate", "Order A La Carte Shop"]} />}
        onGoBackClick={handleGoBack}
      >
        <Formik
          initialValues={{
            shopLocation: "",
            shopType: "",
            employee: "",
            comment: ""
          }}
          validationSchema={getOrderSchema(t)}
          onSubmit={values => {
            handleAddOrderALaCartShop(values);
          }}
          enableReinitialize
        >
          {({
            submitForm,
            isSubmitting,
            values,
            errors,
            touched,
            isValidating
          }) => (
            <AddNewOrderForm
              shopTypes={shopTypes}
              orderCartLocations={orderCartLocations}
              values={values}
              isSubmitting={isSubmitting}
              submitForm={submitForm}
              cancel={handleGoBack}
              errors={errors}
              touched={touched}
              isValidating={isValidating}
            />
          )}
        </Formik>
      </AdminPageLayout>
    );
}

OrderALaCartShop.propTypes = {
  match: PropTypes.any
};
